import * as React from "react";

import { AssistantErrorContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AssistantErrorContent";
import { AssistantTextContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AssistantTextContent";
import { ToolDetailPanelContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/ToolDetailPanel";
import { UserMessage } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/UserMessage";
import { ReconstructedMessage } from "@/shared/domain/admin";
import {
  type AgentThreadMessage,
  type AgentThreadToolResultContent,
  type AgentThreadToolUseContent,
  AgentThreadRole,
  isAgentThreadCompressionFillerContent,
  isAgentThreadDeletedContent,
  isAgentThreadErrorContent,
  isAgentThreadTextContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import { displayToolName } from "@/shared/domain/agentThread/clientTools/ClientTools";

import styles from "./ReconstructedMessageRow.module.css";
import {
  estimateTokens,
  formatTokenDelta,
  formatTokenLabel,
} from "./tokenFormat";

type AgentThreadContent = AgentThreadMessage["content"][number];

/** One block to render: the sent block, its aligned uncompressed original (for
 *  the per-block toggle, when present), and whether deletion dropped it. */
interface RenderUnit {
  block: AgentThreadContent;
  original: AgentThreadContent | null;
  deleted: boolean;
}

const MS_TO_SECONDS_THRESHOLD = 100;

function formatRuntime(runtimeMs: number): string {
  return runtimeMs < MS_TO_SECONDS_THRESHOLD
    ? `${runtimeMs}ms`
    : `${(runtimeMs / 1000).toFixed(2)}s`;
}

/** A tool_use with no captured result (truncated away) still renders a panel. */
function placeholderResult(
  toolUse: AgentThreadToolUseContent,
): AgentThreadToolResultContent {
  return {
    content_type: "tool_result",
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: 0,
    status: "unknown",
  };
}

/** A tool_result whose tool_use was truncated away still renders a panel. */
function placeholderUse(
  toolResult: AgentThreadToolResultContent,
): AgentThreadToolUseContent {
  return {
    content_type: "tool_use",
    tool_name: toolResult.tool_name,
    tool_use_id: toolResult.tool_use_id,
    input: {},
  };
}

/** Find a tool_use_id's result block within a message, or null. */
function findToolResult(
  message: AgentThreadMessage | null | undefined,
  toolUseId: string,
): AgentThreadToolResultContent | null {
  for (const block of message?.content ?? []) {
    if (
      isAgentThreadToolResultContent(block) &&
      block.tool_use_id === toolUseId
    ) {
      return block;
    }
  }
  return null;
}

/**
 * Find a tool result across a reconstructed entry, sent side first. A deleted
 * exchange's sent content is only a tombstone/filler, so its result lives on
 * the verbatim original — fall back there so a deleted tool_use can still
 * render its response in one panel.
 */
function findToolResultInEntry(
  entry: ReconstructedMessage | null | undefined,
  toolUseId: string,
): AgentThreadToolResultContent | null {
  return (
    findToolResult(entry?.message, toolUseId) ??
    findToolResult(entry?.original_message, toolUseId)
  );
}

/**
 * A single text block of a reconstructed message, owning its own
 * compressed/original toggle — the compression view is per content block, not
 * per message, so one turn can mix verbatim and rewritten runs and you flip
 * each independently. A block whose ``original`` differs from what was sent
 * shows `~4.5k -> 2.3k tok` (original → sent), a yellow edge stripe, and a
 * "Show original" toggle; an unchanged or verbatim block shows a single
 * `~x.yk tok` and a green stripe. A ``deleted`` block shows what was dropped
 * (its only available text) with an orange stripe and no toggle — nothing was
 * sent to compare against.
 */
function TextBlock({
  text,
  originalText,
  isUser,
  deleted,
}: {
  text: string;
  /** The aligned original block's text, when this block has an uncompressed
   *  counterpart; null when verbatim, unaligned, or deleted. */
  originalText: string | null;
  isUser: boolean;
  deleted: boolean;
}) {
  const [showOriginal, setShowOriginal] = React.useState(false);
  const compressed = !deleted && originalText !== null && originalText !== text;
  const shown = showOriginal && originalText !== null ? originalText : text;
  const label = compressed
    ? formatTokenDelta(estimateTokens(originalText), estimateTokens(text))
    : formatTokenLabel(estimateTokens(shown));

  return (
    // The token label sits directly above its block, aligned to the same side
    // as the bubble. The edge stripe is per-block — green verbatim, yellow
    // compressed, orange deleted — since one message can mix them.
    <div
      className={styles.textBlock}
      data-role={isUser ? "user" : "assistant"}
      data-compressed={compressed ? "true" : undefined}
      data-deleted={deleted ? "true" : undefined}
    >
      {label !== null && <span className={styles.blockTokens}>{label}</span>}
      {isUser ? (
        <UserMessage text={shown} isFailed={false} />
      ) : (
        <AssistantTextContent
          text={shown}
          isGenerating={false}
          isCancelling={false}
        />
      )}
      {compressed && (
        <div className={styles.blockToggleRow}>
          <button
            type="button"
            className={styles.toggle}
            onClick={() => {
              setShowOriginal((previous) => !previous);
            }}
          >
            {showOriginal ? "Show compressed" : "Show original"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsed "Used Tool X (0.92s · ~1.2k tok)" row; expands to the chat's tool
 * panel. When the tool's *result* was compressed, the summary carries a
 * `compressed` badge and the body offers an in-place toggle to the original —
 * the result lives on its own (skipped) message, so this is where its
 * compression has to surface. A `deleted` exchange instead carries the orange
 * edge and shows its (verbatim) request and result with no toggle: nothing was
 * sent to compare against.
 */
function ToolBlock({
  toolUse,
  resultEntry,
  deleted = false,
}: {
  toolUse: AgentThreadToolUseContent;
  /** The reconstructed message holding this tool's result, if captured. */
  resultEntry: ReconstructedMessage | null;
  /** The deletion pass dropped this exchange from the payload. */
  deleted?: boolean;
}) {
  const [showOriginal, setShowOriginal] = React.useState(false);

  const sentResult = findToolResultInEntry(resultEntry, toolUse.tool_use_id);
  const originalResult = findToolResult(
    resultEntry?.original_message,
    toolUse.tool_use_id,
  );
  // An original result exists only when the result message was compressed; a
  // deleted exchange has no sent result to diff against, so it never toggles.
  const compressedTier =
    !deleted && resultEntry !== null && originalResult !== null
      ? resultEntry.tier
      : null;
  const result =
    (showOriginal && originalResult !== null ? originalResult : sentResult) ??
    placeholderResult(toolUse);

  // When the result was compressed, show original -> sent; else one count.
  const tokenLabel =
    compressedTier !== null && originalResult !== null && sentResult !== null
      ? formatTokenDelta(
          estimateTokens(toolUse) + estimateTokens(originalResult),
          estimateTokens(toolUse) + estimateTokens(sentResult),
        )
      : formatTokenLabel(estimateTokens(toolUse) + estimateTokens(result));

  // Edge-stripe colour: orange deleted, yellow compressed, green verbatim.
  let tier = "original";
  if (deleted) {
    tier = "deleted";
  } else if (compressedTier !== null) {
    tier = "compressed";
  }

  return (
    <details className={styles.toolDetails} data-tier={tier}>
      <summary className={styles.toolSummary}>
        Used Tool <b>{displayToolName(toolUse.tool_name)}</b> (
        {formatRuntime(result.runtime_ms)}
        {tokenLabel === null ? "" : ` · ${tokenLabel}`})
      </summary>
      {compressedTier !== null && (
        <div className={styles.toolToggleRow}>
          <button
            type="button"
            className={styles.toggle}
            onClick={() => {
              setShowOriginal((previous) => !previous);
            }}
          >
            {showOriginal ? "Show compressed result" : "Show original result"}
          </button>
        </div>
      )}
      <ToolDetailPanelContent toolUse={toolUse} toolResult={result} />
    </details>
  );
}

interface ReconstructedMessageRowProps {
  entry: ReconstructedMessage;
  /** The reconstructed message holding each tool result, keyed by tool_use_id —
   *  lets a tool_use render request + response (and the result's compression)
   *  together in one panel. */
  resultEntryByToolUseId: Map<string, ReconstructedMessage>;
  /** tool_use_ids present in the turn — a tool_result with a match is skipped
   *  (already shown beside its tool_use). */
  pairedToolUseIds: Set<string>;
  /** Reveal blocks the deletion pass dropped from the payload. Off by default —
   *  the viewer shows what Bedrock saw, and a deleted block was never sent. When
   *  on, each deleted block shows its original content with an orange marker. */
  showDeleted?: boolean;
}

/**
 * The blocks to render for an entry: the sent block, its aligned original (for
 * the per-block compressed/original toggle), and whether deletion dropped it.
 *
 * Deletion is a *per-block* property, not a per-tier one. A block is dropped
 * either as an `AgentDeletedContent` tombstone (a whole tool pair, or a filler
 * text run — both persisted at the DELETED tier) or, when the whole message was
 * emptied, as a lone `AgentCompressionFillerContent`. In
 * every case the tombstone is replaced by *what was deleted* — its aligned
 * verbatim original — and flagged `deleted` so the row can show it with an
 * orange marker (or hide it when the viewer hasn't opted in). Tombstones sit in
 * place (block i ↔ original block i), so alignment is positional.
 */
function renderUnits(entry: ReconstructedMessage): RenderUnit[] {
  const sent = entry.message.content;
  const original = entry.original_message?.content ?? null;

  // A fully-emptied message: its lone filler stands in for every original
  // block. Without an original there is nothing to show, and the row drops.
  if (sent.some((block) => isAgentThreadCompressionFillerContent(block))) {
    return (original ?? []).map((block) => ({
      block,
      original: null,
      deleted: true,
    }));
  }

  // Tombstones map 1-1 to original positions, so a per-block original is only
  // trustworthy when the counts match (a multi-block text run can collapse to
  // one block); otherwise drop the per-block original rather than mis-pair.
  const aligned =
    original !== null && original.length === sent.length ? original : null;
  return sent.map((block, index) => {
    const originalBlock = aligned?.[index] ?? null;
    if (isAgentThreadDeletedContent(block)) {
      // Show what was dropped (its aligned original), flagged deleted. With no
      // recoverable original the tombstone falls through to render nothing.
      return { block: originalBlock ?? block, original: null, deleted: true };
    }
    return { block, original: originalBlock, deleted: false };
  });
}

/**
 * One message of the reconstructed Bedrock prompt. Text and errors render
 * through the shared chat content components; tool requests and results
 * render through the chat's own ``ToolDetailPanelContent``, collapsed behind
 * a "Used Tool" summary. Compression is shown per block — a compressed text
 * block or tool carries its own edge stripe and before/after token count, a
 * deleted one an orange stripe — rather than as one message-level bar, which
 * mislabels tool-only turns.
 */
export function ReconstructedMessageRow({
  entry,
  resultEntryByToolUseId,
  pairedToolUseIds,
  showDeleted = false,
}: ReconstructedMessageRowProps) {
  const role = entry.message.role;

  const renderBlock = (
    { block, original, deleted }: RenderUnit,
    key: number,
  ): React.ReactNode => {
    // A deleted block was not sent to Bedrock — hide it unless the viewer opted
    // to reveal what was dropped. A fully-deleted message thus renders nothing
    // and its row drops; a partial deletion keeps only its surviving blocks.
    if (deleted && !showDeleted) {
      return null;
    }
    if (isAgentThreadTextContent(block)) {
      const originalText =
        original !== null && isAgentThreadTextContent(original)
          ? original.text
          : null;
      return (
        <TextBlock
          key={key}
          text={block.text}
          originalText={originalText}
          isUser={role === AgentThreadRole.USER}
          deleted={deleted}
        />
      );
    }
    if (isAgentThreadToolUseContent(block)) {
      return (
        <ToolBlock
          key={key}
          toolUse={block}
          resultEntry={resultEntryByToolUseId.get(block.tool_use_id) ?? null}
          deleted={deleted}
        />
      );
    }
    if (isAgentThreadToolResultContent(block)) {
      // Already shown beside its tool_use; render only orphaned results.
      if (pairedToolUseIds.has(block.tool_use_id)) {
        return null;
      }
      return (
        <ToolBlock
          key={key}
          toolUse={placeholderUse(block)}
          resultEntry={entry}
          deleted={deleted}
        />
      );
    }
    if (isAgentThreadErrorContent(block)) {
      return <AssistantErrorContent key={key} error={block} />;
    }
    if (
      isAgentThreadDeletedContent(block) ||
      isAgentThreadCompressionFillerContent(block)
    ) {
      // A residual tombstone/filler — a deleted slot with no recoverable
      // original to show. Drop it; rendering a bare orange bar just leaves an
      // empty "deleted message" row. If it's the message's only block, the
      // row drops entirely.
      return null;
    }
    return null;
  };

  const blocks = renderUnits(entry).map((unit, index) =>
    renderBlock(unit, index),
  );

  // A roboto message whose only block is a tool_result shown elsewhere has
  // nothing left to render — drop the row rather than leave an empty article.
  if (blocks.every((block) => block === null)) {
    return null;
  }

  return (
    <article className={styles.row}>
      {entry.tier === "synthetic" && (
        <div className={styles.badgeRow}>
          <span className={styles.syntheticBadge}>injected</span>
        </div>
      )}
      {blocks}
    </article>
  );
}
